import { useCallback, useState } from "react";
import axios from "axios";
import styled from "styled-components";
import { useDropzone } from "react-dropzone";
import Loader from "react-loader-spinner";
import DeleteIcon from "@material-ui/icons/Delete";

import compressImage from "./compressImage";
import s3PutConfig from "./s3PutConfig";

/*
 * A single featured image for a blog post.
 *
 * The Post model has always had an `image` field, but nothing ever wrote to
 * it: the admin form had no control for it, so all 19 posts share the office
 * photograph as their share image and none is eligible for an Article rich
 * result, which requires an image.
 *
 * Deliberately not PropertyImageUpload. That component manages an ordered
 * gallery with drag-to-reorder and a floorplan flag; a post has exactly one
 * image, and reusing it would mean disabling most of what it does. What the
 * two do share is the part worth sharing - compressImage, and the same
 * presigned-PUT flow against /s3url.
 */

const Wrap = styled.div`
    margin-bottom: 1rem;
`;

const DropArea = styled.div`
    background: #f6f6f6;
    padding: 2rem;
    border: 1px dashed #c2c2c2;
    text-align: center;
    cursor: pointer;
    color: grey;
    font-size: 0.85rem;
`;

const Hint = styled.div`
    font-size: 0.7rem;
    font-weight: 300;
    color: grey;
    margin-top: 0.4rem;
`;

const PreviewWrap = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 1rem;
`;

const Preview = styled.img`
    width: 14rem;
    max-height: 9rem;
    object-fit: cover;
    border: 1px solid #e6e6e6;
`;

const RemoveButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    & svg {
        fill: grey;
        pointer-events: none;
    }
`;

// Configure axios proxy
const axiosInstance = axios.create({ baseURL: process.env.REACT_APP_API_URL });

const Failed = styled.div`
    color: #a93a2c;
    font-size: 0.8rem;
    margin-top: 0.5rem;
`;

export default function PostImageUpload({ image, setImage }) {
    const [isUploading, setIsUploading] = useState(false);
    const [failed, setFailed] = useState("");

    const upload = useCallback(
        async (passedFile) => {
            setFailed("");
            setIsUploading(true);

            try {
                // Shrink first - a phone photograph is several megabytes and
                // would be served at full size to every reader.
                const file = await compressImage(passedFile);

                const res = await axiosInstance.get("/s3url");
                const presignedURL = res.data.uploadURL;

                await axios.put(presignedURL, file, s3PutConfig(res.data, file));

                // The signed URL carries the credentials in its query string;
                // the object itself is everything before the "?".
                setImage(presignedURL.split("?")[0]);
            } catch (error) {
                // Say so rather than silently leaving the field empty, which
                // is what the property uploader does and why a failed upload
                // there looks like nothing happening at all.
                setFailed("That image could not be uploaded. Please try again.");
                console.log(error);
            } finally {
                setIsUploading(false);
            }
        },
        [setImage]
    );

    const onDrop = useCallback(
        (acceptedFiles) => {
            if (acceptedFiles && acceptedFiles.length) upload(acceptedFiles[0]);
        },
        [upload]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: "image/*",
        multiple: false,
    });

    if (image) {
        return (
            <Wrap>
                <PreviewWrap>
                    <Preview src={image} alt="Featured image for this post" />
                    <RemoveButton type="button" onClick={() => setImage("")} title="Remove image">
                        <DeleteIcon />
                    </RemoveButton>
                </PreviewWrap>
                <Hint>Remove the image to replace it with a different one.</Hint>
            </Wrap>
        );
    }

    return (
        <Wrap>
            <DropArea {...getRootProps()}>
                <input {...getInputProps()} />
                {isUploading ? (
                    <Loader type="Puff" color="#c2c2c2" height={30} width={30} />
                ) : isDragActive ? (
                    <span>Drop the image here ...</span>
                ) : (
                    <span>Drag 'n' drop an image here, or click to choose one</span>
                )}
            </DropArea>
            <Hint>
                Optional. Used as the post's share image on WhatsApp, Facebook and LinkedIn, and shown at
                the top of the article. Large photographs are resized automatically — but the article
                displays it about 900px wide, so start from something at least that big or it will look
                soft.
            </Hint>
            {failed && <Failed>{failed}</Failed>}
        </Wrap>
    );
}
